import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IconEdit } from "@tabler/icons-react";
import Breadcrumb from "../../../components/UI/Breadcrumb/Breadcrumb";
import Button from "../../../components/UI/Button/button";
import MessagesHero from "./User/MessagesHero";
import ThreadListPanel from "./User/ThreadListPanel";
import ConversationDetail from "./User/ConversationDetail";
import MobileConversationModal from "./User/MobileConversationModal";
import NewMessageModal from "../ClientMessages/User/NewMessageModal";
import styles from "./User/TalentMessages.module.css";
import useMediaQuery from "../../../hooks/useMediaQuery";
import {
  talentGetConversations,
  talentGetMessages,
  talentSendMessage,
  talentCreateOrGetConversation,
  talentDeleteConversation,
  getSupportComplaints,
  getSupportComplaintById,
  postSupportComplaint,
  replySupportComplaint,
} from "../../../services/talentCalls";
import { confirmationHandler } from "../../../utils/confirmationHandler";
import { errorHandler, toastSuccess } from "../../../utils";
import { DocumentHeader } from "../../../components/Seo";

function matchesSearch(thread, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    thread.subject.toLowerCase().includes(q) ||
    thread.sender.toLowerCase().includes(q) ||
    thread.preview.toLowerCase().includes(q)
  );
}

const PER_PAGE = 10;
const BACKEND_PAGE_SIZE = 20;

function formatDayAndClock(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const clock = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return { day, clock };
}

function formatMessageForBubbles(msg) {
  const outgoing = msg?.senderType === "Talent";
  const sender = msg?.sender || msg?.senderId || {};
  const fromName = outgoing ? "You" : `${sender?.firstName || ""} ${sender?.lastName || ""}`.trim() || "Client";
  const initials = outgoing
    ? "Y"
    : `${(sender?.firstName || "C").charAt(0)}${(sender?.lastName || "").charAt(0)}`.toUpperCase();
  const { day, clock } = formatDayAndClock(msg?.createdAt);
  return {
    from: fromName,
    initials,
    time: `${day} · ${clock}`,
    text: msg?.content || "",
    outgoing,
  };
}

function formatThread(conv) {
  const latest = conv?.messages || conv?.latestMessages || [];
  const lastMessage = latest.length
    ? latest.reduce((a, b) => (new Date(b?.createdAt) > new Date(a?.createdAt) ? b : a))
    : null;
  const client = conv?.clientId || {};
  const senderName =
    conv?.participantName || `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "Client";
  const senderEmail = conv?.participantEmail || client?.email || "Email not available";
  const refDate = lastMessage?.createdAt || conv?.lastMessageAt || conv?.createdAt;
  const { day, clock } = formatDayAndClock(refDate);

  return {
    id: conv?._id,
    subject: conv?.subject || "No subject",
    sender: senderName,
    email: senderEmail,
    type: "Client",
    badgeClass: "badgeClient",
    date: day,
    timestamp: clock,
    lastActivityAt: refDate,
    preview: lastMessage?.content || conv?.lastMessage || "No messages yet",
    unread: conv?.hasUnread ?? conv?.unread ?? false,
    color: Math.abs(hashCode(conv?._id || senderEmail)) % 5,
    messages: latest.map(formatMessageForBubbles),
  };
}

/* ── Support requests ──
   These come from /api/talent/support and have their own shape, so they get
   their own formatter onto the same thread structure the list already uses. */
function supportRepliesOf(entry) {
  return [
    ...(entry?.replies || []).map((reply) => ({ ...reply, fromSupport: true })),
    ...(entry?.repliesToAdminSupport || []).map((reply) => ({
      ...reply,
      fromSupport: false,
    })),
  ].sort((a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0));
}

function formatSupportThread(entry) {
  const replies = supportRepliesOf(entry);
  const lastReply = replies.length ? replies[replies.length - 1] : null;
  const refDate = lastReply?.createdAt || entry?.updatedAt || entry?.createdAt;
  const { day, clock } = formatDayAndClock(refDate);
  const opened = formatDayAndClock(entry?.createdAt);
  const adminStarted = entry?.supportType === "AdminSupport";

  const messages = [
    {
      from: adminStarted ? "Awasource Support" : "You",
      initials: adminStarted ? "AS" : "Y",
      time: `${opened.day} · ${opened.clock}`,
      text: entry?.message || "",
      outgoing: !adminStarted,
    },
    ...replies.map((reply) => {
      const t = formatDayAndClock(reply?.createdAt);
      const fromSupport =
        reply?.fromSupport !== false && Boolean(reply?.adminId || reply?.adminName);
      return {
        from: fromSupport ? "Awasource Support" : "You",
        initials: fromSupport ? "AS" : "Y",
        time: `${t.day} · ${t.clock}`,
        text: reply?.message || "",
        outgoing: !fromSupport,
      };
    }),
  ].filter((m) => m.text);

  return {
    id: entry?._id,
    isSupport: true,
    subject: entry?.title || entry?.supportType || "Support request",
    sender: "Awasource Support",
    email: "support@awasource.com",
    type: entry?.supportType || "Support",
    badgeClass: "badgeSupport",
    date: day,
    timestamp: clock,
    lastActivityAt: refDate,
    // adminSupportStatus is the progress of the request; `status` is read/unread.
    status: entry?.adminSupportStatus || "",
    preview: lastReply?.message || entry?.message || "No messages yet",
    unread: entry?.status !== "read",
    color: 0,
    messages,
  };
}

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

const MessagesRedesign = () => {
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery(900);

  const startOnSupport = searchParams.get("tab") === "support";

  const [allThreads, setAllThreads] = useState({ user: [] });
  const [supportThreads, setSupportThreads] = useState([]);
  const [loadingSupport, setLoadingSupport] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentInbox, setCurrentInbox] = useState(startOnSupport ? "support" : "user");
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);

  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [mobileThreadId, setMobileThreadId] = useState(null);
  const [mobileReplyText, setMobileReplyText] = useState("");

  const [newMsgOpen, setNewMsgOpen] = useState(false);

  const fetchConversations = async () => {
    setLoadingThreads(true);
    try {
      const first = await talentGetConversations(1, BACKEND_PAGE_SIZE);
      const body = first?.data;
      let list = body?.data?.conversations || body?.conversations || [];
      const total = body?.data?.pagination?.total ?? body?.data?.total ?? list.length;

      if (list.length > 0 && list.length < total) {
        const totalBackendPages = Math.ceil(total / list.length);
        const remainingPages = Array.from({ length: totalBackendPages - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(remainingPages.map((p) => talentGetConversations(p, list.length)));
        rest.forEach((res) => {
          const b = res?.data;
          list = list.concat(b?.data?.conversations || b?.conversations || []);
        });
      }

      setAllThreads({ user: list.map(formatThread) });
    } catch (_) {
      setAllThreads({ user: [] });
    } finally {
      setLoadingThreads(false);
    }
  };

  const fetchSupportThreads = async () => {
    setLoadingSupport(true);
    try {
      const res = await getSupportComplaints(1, BACKEND_PAGE_SIZE);
      const records = res?.data?.data?.records || [];
      setSupportThreads(records.map(formatSupportThread));
    } catch (_) {
      setSupportThreads([]);
    } finally {
      setLoadingSupport(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchSupportThreads();
  }, []);

  useEffect(() => {
    if (!isMobile && allThreads.user.length > 0 && currentThreadId === null) {
      setCurrentThreadId(allThreads.user[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allThreads.user]);

  const isSupportInbox = currentInbox === "support";
  const inboxThreads = isSupportInbox ? supportThreads : allThreads.user;
  const filteredThreads = inboxThreads.filter((t) => matchesSearch(t, searchQuery));
  const totalPages = Math.ceil(filteredThreads.length / PER_PAGE) || 1;
  const start = (currentPage - 1) * PER_PAGE;
  const pagedThreads = filteredThreads.slice(start, start + PER_PAGE);
  const total = filteredThreads.length;
  const pageInfoText =
    total === 0
      ? isSupportInbox
        ? "0 support requests"
        : "0 messages"
      : `${start + 1}–${Math.min(start + PER_PAGE, total)} of ${total}`;

  const displayThreads = pagedThreads;
  const selectedThread =
    displayThreads.find((t) => t.id === currentThreadId) ||
    displayThreads[0] ||
    null;
  const mobileThread = displayThreads.find((t) => t.id === mobileThreadId) || null;

  /* The hero covers both inboxes — "Manage conversations with clients and
     support in one place" — so the counts span client threads and support
     requests together, not just the user inbox. */
  const everyThread = [...allThreads.user, ...supportThreads];
  const statTotal = everyThread.length;
  const statUnread = everyThread.filter((t) => t.unread).length;
  const statToday = everyThread.filter((t) => {
    if (!t?.lastActivityAt) return false;
    const d = new Date(t.lastActivityAt);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  }).length;

  function updateThread(id, updater) {
    setAllThreads((prev) => ({ ...prev, user: prev.user.map((t) => (t.id === id ? updater(t) : t)) }));
  }

  const fetchThreadMessages = async (id) => {
    try {
      const res = await talentGetMessages(id, 1);
      const list = res?.data?.messages || res?.messages || [];
      if (list.length) {
        const sorted = [...list].sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
        updateThread(id, (t) => ({ ...t, messages: sorted.map(formatMessageForBubbles) }));
      }
    } catch (_) {}
  };

  function handleSwitchInbox(type) {
    setCurrentInbox(type);
    setCurrentPage(1);
    if (type === "support") {
      setCurrentThreadId(supportThreads[0]?.id ?? null);
    } else {
      setCurrentThreadId(allThreads.user[0]?.id ?? null);
    }
  }

  function handleSearchChange(value) {
    setSearchQuery(value);
    setCurrentPage(1);
  }

  function handlePrevPage() {
    setCurrentPage((p) => Math.max(1, p - 1));
  }
  function handleNextPage() {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  }
  function handleGoPage(p) {
    setCurrentPage(p);
  }

  // Pulls the full support request (with all replies) and refreshes that row.
  const fetchSupportThread = async (id) => {
    try {
      const res = await getSupportComplaintById(id);
      const entry = res?.data?.data?.supportEntry;
      if (entry) {
        const fresh = formatSupportThread(entry);
        setSupportThreads((prev) =>
          prev.map((t) => (t.id === id ? { ...fresh, unread: false } : t)),
        );
      }
    } catch (_) {}
  };

  function handleThreadClick(id) {
    if (isSupportInbox) {
      setSupportThreads((prev) =>
        prev.map((t) => (t.id === id ? { ...t, unread: false } : t)),
      );
      if (isMobile) {
        setMobileThreadId(id);
        setMobileReplyText("");
        setMobileModalOpen(true);
      } else {
        setCurrentThreadId(id);
      }
      fetchSupportThread(id);
      return;
    }
    if (isMobile) {
      updateThread(id, (t) => ({ ...t, unread: false }));
      setMobileThreadId(id);
      setMobileReplyText("");
      setMobileModalOpen(true);
    } else {
      updateThread(id, (t) => ({ ...t, unread: false }));
      setCurrentThreadId(id);
      setReplyText("");
    }
    fetchThreadMessages(id);
  }

  async function handleSendReply() {
    const text = replyText.trim();
    if (!text || currentThreadId === null) return;
    setSending(true);
    try {
      if (isSupportInbox) {
        await replySupportComplaint(currentThreadId, { message: text });
        setReplyText("");
        await fetchSupportThread(currentThreadId);
        return;
      }
      await talentSendMessage({
        conversationId: currentThreadId,
        receiverEmail: selectedThread?.email,
        content: text,
      });
      setReplyText("");
      await fetchThreadMessages(currentThreadId);
    } catch (_) {
      errorHandler("Couldn't send your reply. Please try again.", "Error");
    } finally {
      setSending(false);
    }
  }

  function handleMarkUnread() {
    if (isSupportInbox || currentThreadId === null) return;
    updateThread(currentThreadId, (t) => ({ ...t, unread: true }));
  }

  function removeCurrentThread() {
    if (isSupportInbox || currentThreadId === null) return;
    const remaining = inboxThreads.filter((t) => t.id !== currentThreadId);
    setAllThreads({ user: remaining });
    const remainingFiltered = remaining.filter((t) => matchesSearch(t, searchQuery));
    setCurrentThreadId(remainingFiltered.length > 0 && !isMobile ? remainingFiltered[0].id : null);
  }

  function handleArchive() {
    removeCurrentThread();
  }

  async function handleDelete() {
    if (isSupportInbox || currentThreadId === null) return;
    const ok = await confirmationHandler("Delete this conversation? This cannot be undone.");
    if (!ok) return;
    try {
      await talentDeleteConversation(currentThreadId);
    } catch (_) {
      errorHandler("Couldn't delete this conversation. Please try again.", "Error");
      return;
    }
    removeCurrentThread();
  }

  function handleCloseMobileModal() {
    setMobileModalOpen(false);
    setMobileThreadId(null);
  }

  async function handleSendMobileReply() {
    const text = mobileReplyText.trim();
    if (!text || mobileThreadId === null) return;
    setSending(true);
    try {
      if (isSupportInbox) {
        await replySupportComplaint(mobileThreadId, { message: text });
        setMobileReplyText("");
        await fetchSupportThread(mobileThreadId);
        return;
      }
      await talentSendMessage({
        conversationId: mobileThreadId,
        receiverEmail: mobileThread?.email,
        content: text,
      });
      setMobileReplyText("");
      await fetchThreadMessages(mobileThreadId);
    } catch (_) {
      errorHandler("Couldn't send your reply. Please try again.", "Error");
    } finally {
      setSending(false);
    }
  }

  async function handleComposeSubmit(form) {
    if (form.inbox === "Support Inbox") {
      if (!form.subject) {
        errorHandler("Subject is required.", "Error");
        return false;
      }
      if (!form.message.trim()) {
        errorHandler("Message content is required.", "Error");
        return false;
      }
      setSending(true);
      try {
        const firstName = localStorage.getItem("firstName") || "";
        const lastName = localStorage.getItem("lastName") || "";
        const email = localStorage.getItem("email") || "";
        await postSupportComplaint({
          supportType: "Contact",
          fullName: `${firstName} ${lastName}`.trim() || email,
          email,
          subject: form.subject,
          message: form.message.trim(),
        });
        toastSuccess("Support request sent");
        setNewMsgOpen(false);
        await fetchSupportThreads();
        setCurrentInbox("support");
        return true;
      } catch (_) {
        errorHandler("Failed to send your support request.", "Error");
        return false;
      } finally {
        setSending(false);
      }
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.to || !emailRe.test(form.to)) {
      errorHandler("Enter a valid email address.", "Error");
      return false;
    }
    if (!form.subject) {
      errorHandler("Subject is required.", "Error");
      return false;
    }
    if (!form.message.trim()) {
      errorHandler("Message content is required.", "Error");
      return false;
    }

    setSending(true);
    try {
      const convRes = await talentCreateOrGetConversation({ participantEmail: form.to, subject: form.subject });
      const conversationId = convRes?.data?.data?._id || convRes?.data?._id || convRes?._id;
      if (!conversationId) {
        errorHandler("Failed to start the conversation.", "Error");
        return false;
      }
      await talentSendMessage({ conversationId, receiverEmail: form.to, content: form.message });
      toastSuccess("Message sent");
      setNewMsgOpen(false);
      await fetchConversations();
      return true;
    } catch (error) {
      if (error?.response?.status === 404) errorHandler("No client found with this email.", "Error");
      else errorHandler("Failed to send message.", "Error");
      return false;
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={styles.main}>
      <DocumentHeader title="Awasource | Messages" />

      <Breadcrumb
        crumbs={[{ label: "Dashboard", href: "/talent-dashboard" }, { label: "Messages" }]}
        title="Messages"
        actions={
          <Button variant="primary" leftIcon={<IconEdit size={16} />} onClick={() => setNewMsgOpen(true)}>
            New Message
          </Button>
        }
      />

      <MessagesHero
        total={statTotal}
        unread={statUnread}
        today={statToday}
      />

      <div className={styles.msgLayout}>
        <ThreadListPanel
          currentInbox={currentInbox}
          onSwitchInbox={handleSwitchInbox}
          userCount={allThreads.user.length}
          supportCount={supportThreads.length}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          pagedThreads={loadingThreads && !isSupportInbox ? [] : displayThreads}
          selectedThreadId={currentThreadId}
          onThreadClick={handleThreadClick}
          pageInfoText={pageInfoText}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          onGoPage={handleGoPage}
        />

        <ConversationDetail
          thread={selectedThread}
          replyText={replyText}
          onReplyChange={setReplyText}
          onSend={handleSendReply}
          onMarkUnread={handleMarkUnread}
          onArchive={handleArchive}
          onDelete={handleDelete}
          hideActions={isSupportInbox}
        />
      </div>

      <MobileConversationModal
        open={mobileModalOpen}
        thread={mobileThread}
        replyText={mobileReplyText}
        onReplyChange={setMobileReplyText}
        onSend={handleSendMobileReply}
        onClose={handleCloseMobileModal}
        readOnly={false}
      />

      <NewMessageModal
        open={newMsgOpen}
        onClose={() => setNewMsgOpen(false)}
        onSubmit={handleComposeSubmit}
        sending={sending}
      />
    </div>
  );
};

export default MessagesRedesign;
